import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import { getOperators, inputTypeFor } from './FilterEngine';
import type { FilterCondition, FilterDataType, FilterOperator } from './types';

export interface CustomFilterDialogProps {
  open: boolean;
  columnLabel: string;
  dataType: FilterDataType;
  allowedOperators?: FilterOperator[];
  position?: { top: number; left: number };
  initialOperator?: FilterOperator;
  initialCondition?: FilterCondition | null;
  onCancel: () => void;
  onApply: (condition: FilterCondition) => void;
}

const NAVY = '#003771';

export const CustomFilterDialog: React.FC<CustomFilterDialogProps> = ({
  open,
  columnLabel,
  dataType,
  allowedOperators,
  position,
  initialOperator,
  initialCondition,
  onCancel,
  onApply,
}) => {
  const theme = useTheme();
  const colors = theme.palette as any;
  const filterColors = colors.filterDropdown;
  const operators = getOperators(dataType).filter(
    (o) => !allowedOperators || allowedOperators.includes(o.operator)
  );
  const fallback = initialOperator ?? initialCondition?.operator ?? operators[0]?.operator;

  const [operator, setOperator] = useState<FilterOperator | undefined>(fallback);
  const [value1, setValue1] = useState<string>(String(initialCondition?.value1 ?? ''));
  const [value2, setValue2] = useState<string>(String(initialCondition?.value2 ?? ''));
  const [isOperatorMenuOpen, setIsOperatorMenuOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    setOperator(initialOperator ?? initialCondition?.operator ?? operators[0]?.operator);
    setValue1(initialCondition?.value1 != null ? String(initialCondition.value1) : '');
    setValue2(initialCondition?.value2 != null ? String(initialCondition.value2) : '');
  }, [open, initialOperator, initialCondition]);

  const definition = operators.find((o) => o.operator === operator);
  const inputs = definition?.inputs ?? 1;
  const inputType = inputTypeFor(dataType);

  const canApply =
    Boolean(operator) &&
    (inputs === 0 ||
      (inputs === 1 && value1.trim() !== '') ||
      (inputs === 2 && value1.trim() !== '' && value2.trim() !== ''));

  const handleApply = () => {
    if (!operator || !canApply) return;
    onApply({
      operator,
      value1: inputs === 0 ? null : value1.trim(),
      value2: inputs === 2 ? value2.trim() : null,
    });
  };

  const handleCancelClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    onCancel();
  };

  return (
    <Dialog
      open={open}
      onClose={() => undefined}
      maxWidth={false}
      fullWidth
      aria-labelledby="custom-filter-title"
      PaperProps={{
        sx: {
          width: 680,
          maxWidth: 'calc(100vw - 32px)',
          borderRadius: '2px',
          overflow: 'hidden',
          ...(position && {
            position: 'fixed',
            top: position.top,
            left: position.left,
            m: 0,
          }),
        },
      }}
    >
      <Box
        sx={{
          backgroundColor: NAVY,
          color: '#fff',
          px: 3,
          py: 1.25,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography id="custom-filter-title" sx={{ fontSize: 15, fontWeight: 600 }}>
          Custom Filter
        </Typography>
        <IconButton size="small" onMouseDown={handleCancelClick} aria-label="Close" sx={{ color: '#fff' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ backgroundColor: '#EFF6FF', px: 3, pt: 2.5, pb: 2 }}>
        <Typography sx={{ fontSize: 13, color: filterColors.searchIcon, mb: 1.75 }}>
          Show items where:{' '}
          <Box component="span" sx={{ fontWeight: 700, color: '#12253B' }}>
            {columnLabel}
          </Box>
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'nowrap', width: '100%' }}>
          <Select
            size="small"
            value={operator ?? ''}
            open={isOperatorMenuOpen}
            onOpen={() => setIsOperatorMenuOpen(true)}
            onClose={() => setIsOperatorMenuOpen(false)}
            onChange={(e) => setOperator(e.target.value as FilterOperator)}
            sx={{
              width: 280,
              minWidth: 280,
              height: 44,
              backgroundColor: `${filterColors.fieldBackground} !important`,
              borderRadius: '4px',
              color: `${colors.inputNew.inputText} !important`,
              '& .MuiSelect-select': {
                py: 1.25,
                color: `${colors.inputNew.inputText} !important`,
              },
              '& .MuiSelect-icon': {
                color: `${filterColors.fieldIcon} !important`,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: `${filterColors.fieldBorder} !important`,
              },
            }}
            MenuProps={{
              sx: { zIndex: 1600 },
              PaperProps: {
                sx: {
                  zIndex: 1600,
                  backgroundColor: `${filterColors.panelBackground} !important`,
                  '& .MuiMenu-list': {
                    backgroundColor: `${filterColors.panelBackground} !important`,
                    padding: 0,
                  },
                  '& .MuiMenuItem-root': {
                    color: `${colors.inputNew.inputText} !important`,
                    backgroundColor: `${filterColors.panelBackground} !important`,
                    '&.Mui-selected': {
                      backgroundColor: `${filterColors.checkboxCheck} !important`,
                      color: `${colors.inputNew.inputText} !important`,
                    },
                    '&:hover': {
                      backgroundColor: `${filterColors.optionHoverBackground} !important`,
                    },
                  },
                },
              },
            }}
            inputProps={{ 'aria-label': 'Filter condition' }}
          >
            {operators.map((o) => (
              <MenuItem
                key={o.operator}
                value={o.operator}
                onMouseDown={(event) => {
                  if (event.button !== 0) return;
                  event.preventDefault();
                  setOperator(o.operator);
                  setIsOperatorMenuOpen(false);
                }}
              >
                {o.label}
              </MenuItem>
            ))}
          </Select>

          {inputs === 1 && (
            <TextField
              size="small"
              type={inputType}
              value={value1}
              placeholder="Enter value"
              onChange={(e) => setValue1(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleApply()}
              sx={{
                flex: 1,
                minWidth: 0,
                backgroundColor: `${filterColors.fieldBackground} !important`,
                borderRadius: '4px',
                '& .MuiInputBase-root': { height: 44 },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: `${filterColors.fieldBorder} !important`,
                },
                '& .MuiInputBase-input': {
                  color: `${colors.inputNew.inputText} !important`,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
                '& .MuiInputLabel-root': {
                  color: `${filterColors.searchText} !important`,
                },
              }}
              inputProps={{
                'aria-label': 'Filter value',
                title: value1 || undefined,
              }}
            />
          )}

          {inputs === 2 && (
            <>
              <TextField
                size="small"
                type={inputType}
                value={value1}
                placeholder="Enter value"
                onChange={(e) => setValue1(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleApply()}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  backgroundColor: `${filterColors.fieldBackground} !important`,
                  borderRadius: '4px',
                  '& .MuiInputBase-root': { height: 44 },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: `${filterColors.fieldBorder} !important`,
                  },
                  '& .MuiInputBase-input': {
                    color: `${colors.inputNew.inputText} !important`,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                  '& .MuiInputLabel-root': {
                    color: `${filterColors.searchText} !important`,
                  },
                }}
                inputProps={{
                  'aria-label': 'First filter value',
                  title: value1 || undefined,
                }}
              />
              <Typography sx={{ color: `${colors.inputNew.inputText} !important`, fontSize: 14, flexShrink: 0 }}>&amp;</Typography>
              <TextField
                size="small"
                type={inputType}
                value={value2}
                placeholder="Enter value"
                onChange={(e) => setValue2(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleApply()}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  backgroundColor: `${filterColors.fieldBackground} !important`,
                  borderRadius: '4px',
                  '& .MuiInputBase-root': { height: 44 },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: `${filterColors.fieldBorder} !important`,
                  },
                  '& .MuiInputBase-input': {
                    color: `${colors.inputNew.inputText} !important`,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                  '& .MuiInputLabel-root': {
                    color: `${filterColors.searchText} !important`,
                  },
                }}
                inputProps={{
                  'aria-label': 'Second filter value',
                  title: value2 || undefined,
                }}
              />
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ backgroundColor: '#EFF6FF', px: 3, pb: 2.5, pt: 1 }}>
        <Button onMouseDown={handleCancelClick} sx={{ color: '#0087C4', fontWeight: 600, minWidth: 84 }}>
          CANCEL
        </Button>
        <Button
          variant="contained"
          disabled={!canApply}
          onMouseDown={handleApply}
          sx={{
            backgroundColor: '#0087C4',
            minWidth: 106,
            height: 36,
            '&:hover': { backgroundColor: '#0087C4' },
            '&.Mui-disabled': {
              color: '#FFFFFF !important',
              backgroundColor: '#A9B8C8 !important',
            },
          }}
        >
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomFilterDialog;